/**
 * System health — diagnostics screen. See frontend spec §5.9 + Midnight Ops.
 *
 * Answers, in layout priority:
 *   1. Overall readiness — READY / DEGRADED / FAILED (authoritative)
 *   2. Dependency health — database, Keka, process (liveness is secondary)
 *   3. Environment information — values from GET /health only
 *
 * Data sources (only these — never invent metrics or pull sync settings):
 *   - GET /health/live
 *   - GET /health/ready  (may be 503 with a parseable degraded body)
 *   - GET /health
 *
 * Failed/degraded dependencies use left-border indicators. Status is colored
 * UI-font text (no badge pills). Auto-polls every ~30s.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState } from "../components/states";
import { renderStatusText, checkTone } from "../components/statusText";
import { EMPTY } from "../utils/format";
import { getHealthInfo, getLiveness, getReadiness } from "../services/health";
import type {
  HealthInfoBody,
  HealthLiveBody,
  HealthReadyBody,
} from "../types/api";
import type { HealthResult } from "../services/apiClient";

const REFRESH_INTERVAL_MS = 30_000;

type ReadyState = "READY" | "DEGRADED" | "FAILED";

export class SystemHealthPage {
  private intervalId: number | null = null;

  private readinessBody: HTMLElement = el("div", {}, [renderLoadingState("Checking system status…")]);
  private depsBody: HTMLElement = el("div", {}, [renderLoadingState("Checking dependencies…")]);
  private envBody: HTMLElement = el("div", {}, [renderLoadingState("Loading environment…")]);

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    const refreshButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        "aria-label": "Refresh system health",
        onclick: () => void this.refreshAll(),
      },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "System Health",
          subtitle: "Whether the platform is operational and able to serve requests right now.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [refreshButton],
        }),
        el("div", { className: "panel panel--dominant" }, [
          el("p", { className: "panel__title" }, ["Overall system status"]),
          el("p", { className: "panel__description" }, [
            "Whether the platform can serve traffic right now.",
          ]),
          this.readinessBody,
        ]),
        el("div", { className: "panel panel--standard" }, [
          el("p", { className: "panel__title" }, ["Dependency health"]),
          el("p", { className: "panel__description" }, [
            "Database and Keka connection status, plus whether the process is up.",
          ]),
          this.depsBody,
        ]),
        el("div", { className: "panel panel--standard" }, [
          el("p", { className: "panel__title" }, ["Environment"]),
          el("p", { className: "panel__description" }, [
            "Who this instance is: environment, version, and related runtime details.",
          ]),
          this.envBody,
        ]),
      ])
    );

    void this.refreshAll();
    this.intervalId = window.setInterval(() => void this.refreshAll(), REFRESH_INTERVAL_MS);
  }

  destroy(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private async refreshAll(): Promise<void> {
    const [ready, live, info] = await Promise.all([getReadiness(), getLiveness(), getHealthInfo()]);

    this.renderReadiness(ready);
    this.renderDependencies(ready, live);
    this.renderEnvironment(info);
  }

  /* ---------------- 1. Overall readiness ---------------- */

  private renderReadiness(ready: HealthResult<HealthReadyBody>): void {
    if (!ready.body) {
      mount(
        this.readinessBody,
        el("div", {}, [
          el("div", { className: "health-panel__primary" }, [
            el("span", { className: "health-panel__primary-label" }, ["Status"]),
            el(
              "span",
              {
                className: "health-panel__primary-value health-panel__primary-value--failed",
                role: "status",
              },
              ["FAILED"]
            ),
            el("span", { className: "health-panel__primary-hint" }, [
              ready.status === 0
                ? "Could not reach the readiness check. The platform did not respond."
                : `Could not parse /health/ready (HTTP ${ready.status}).`,
            ]),
          ]),
          el("div", { className: "mt-6" }, [
            el(
              "button",
              {
                className: "btn btn-ghost btn-sm",
                type: "button",
                "aria-label": "Retry readiness check",
                onclick: () => void this.refreshAll(),
              },
              ["Retry"]
            ),
          ]),
        ])
      );
      return;
    }

    const state = overallState(ready.body);
    const failing = failingDependencies(ready.body);
    const valueClass =
      state === "READY"
        ? "health-panel__primary-value--ok"
        : state === "DEGRADED"
          ? "health-panel__primary-value--degraded"
          : "health-panel__primary-value--failed";

    const hint =
      state === "READY"
        ? "All system checks are passing."
        : failing.length === 0
          ? "System reported a degraded status."
          : failing.length === 1
            ? `${failing[0]} is causing degradation.`
            : `${failing.join(" and ")} are causing degradation.`;

    mount(
      this.readinessBody,
      el("div", { className: "health-panel__primary" }, [
        el("span", { className: "health-panel__primary-label" }, ["Status"]),
        el("span", { className: `health-panel__primary-value ${valueClass}`, role: "status" }, [state]),
        el("span", { className: "health-panel__primary-hint" }, [hint]),
      ])
    );
  }

  /* ---------------- 2. Dependency health ---------------- */

  private renderDependencies(
    ready: HealthResult<HealthReadyBody>,
    live: HealthResult<HealthLiveBody>
  ): void {
    const rows: HTMLElement[] = [];

    if (ready.body?.checks) {
      rows.push(dependencyRow("Database", ready.body.checks.database, "readiness"));
      rows.push(dependencyRow("Keka connection", ready.body.checks.kekaAuth, "readiness"));
    } else {
      rows.push(dependencyRow("Database", null, "readiness"));
      rows.push(dependencyRow("Keka connection", null, "readiness"));
    }

    if (live.body) {
      const ok = live.body.status === "ok";
      rows.push(dependencyRow("Process", ok ? "ok" : "failed", "liveness"));
    } else {
      rows.push(dependencyRow("Process", null, "liveness"));
    }

    const table = el("table", { className: "data-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { scope: "col" }, ["Dependency"]),
          el("th", { scope: "col" }, ["Status"]),
          el("th", { scope: "col" }, ["Source"]),
        ]),
      ]),
      el("tbody", {}, rows),
    ]);

    mount(this.depsBody, el("div", { className: "data-table-wrap" }, [table]));
  }

  /* ---------------- 3. Environment ---------------- */

  private renderEnvironment(info: HealthResult<HealthInfoBody>): void {
    if (!info.body) {
      mount(
        this.envBody,
        renderErrorState("Could not reach /health.", { onRetry: () => void this.refreshAll() })
      );
      return;
    }

    const env = info.body.environment;
    mount(
      this.envBody,
      el("div", { className: "next-cron" }, [
        infoRow("Environment", env ?? EMPTY, true),
        infoRow("Version", info.body.version ?? EMPTY, true),
        infoRow("Uptime", formatUptime(info.body.uptimeSeconds) ?? EMPTY),
        infoRow("Status", info.body.status ?? EMPTY),
      ])
    );
  }

}

/* ---------------- helpers ---------------- */

function overallState(body: HealthReadyBody): ReadyState {
  if (body.status === "ok") return "READY";
  if (body.status === "degraded") return "DEGRADED";
  return "FAILED";
}

function failingDependencies(body: HealthReadyBody): string[] {
  const failing: string[] = [];
  if (body.checks?.database === "failed") failing.push("Database");
  if (body.checks?.kekaAuth === "failed") failing.push("Keka connection");
  return failing;
}

function dependencyRow(
  name: string,
  value: "ok" | "failed" | null,
  source: "readiness" | "liveness"
): HTMLElement {
  const tone = value === null ? { label: "unknown", tone: "muted" as const } : checkTone(value);
  const flagClass =
    value === "failed" ? "flag-danger" : value === null ? "flag-accent" : "";

  const statusLabel =
    source === "liveness" && value === "ok"
      ? "live"
      : source === "liveness" && value === "failed"
        ? "not responding"
        : tone.label;

  return el("tr", { className: flagClass || undefined }, [
    el("td", {}, [name]),
    el("td", {}, [renderStatusText(statusLabel, tone.tone)]),
    el("td", { className: "data-table__cell-muted" }, [
      source === "readiness" ? "System status" : "Process check",
    ]),
  ]);
}

function infoRow(label: string, value: string, technical = false): HTMLElement {
  return el("div", { className: "next-cron__row" }, [
    el("span", { className: "next-cron__label" }, [label]),
    el("span", { className: technical ? "next-cron__value mono" : "next-cron__value" }, [value]),
  ]);
}

function formatUptime(seconds: number | undefined): string | null {
  if (seconds === undefined || seconds === null || Number.isNaN(seconds)) return null;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
